// Aggregates parsed Etsy CSV rows into a complete report.
//
// Etsy CSV column variants seen in the wild:
//   "Sale Date" / "Date" / "Sold Date"
//   "Item Name" / "Title" / "Listing Title"
//   "Number Sold" / "Quantity"
//   "Item Total" / "Item Price" / "Subtotal"
//   "Shipping" / "Shipping Cost" / "Ship Cost"
//   "Order Net" / "Net Amount" / "Net"
//   "Card Processing Fees" / "Processing Fees"
//   "Sales Tax" / "Tax"
//   "Status" — "Sold", "Refunded", etc.
//   "Currency" — three-letter code
//   "Order ID" — for deduplication
//   "Discount Amount" / "Discount"
//
// We accept any subset and fall back to the calculator's fee model when the
// CSV doesn't have explicit fee columns.
//
// PUBLIC API:
//   buildReport(parsedRows, settings, costOverrides)
//     → one-time initial pass: normalize + aggregate at month granularity
//   aggregateFromSales(sales, settings, opts)
//     → re-aggregate already-normalized sales (used after filtering)
//   filterSales(sales, range)
//     → return subset of sales whose date falls in [range.start, range.end]
//   bucketSales(sales, granularity)
//     → group sales into day / week / month / quarter buckets
//   computePriorRange(range)
//     → return the equivalent prior period for comparison
//   pickGranularity(range)
//     → suggest a sensible granularity for a given date range

import { pickField, parseAmount, parseDate } from './csvParser';

const FIELD_MAP = {
  date:       ['Sale Date', 'Date', 'Sold Date', 'Order Date'],
  itemName:   ['Item Name', 'Title', 'Listing Title', 'Product Name'],
  quantity:   ['Number Sold', 'Quantity', 'Qty'],
  itemTotal:  ['Item Total', 'Item Price', 'Subtotal', 'Price'],
  shipping:   ['Shipping', 'Shipping Cost', 'Ship Cost', 'Shipping Charge'],
  orderNet:   ['Order Net', 'Net Amount', 'Net', 'Net Earnings'],
  cardFees:   ['Card Processing Fees', 'Processing Fees', 'Payment Fees'],
  salesTax:   ['Sales Tax', 'Tax', 'VAT'],
  status:     ['Status', 'Order Status'],
  currency:   ['Currency'],
  orderId:    ['Order ID', 'OrderId', 'Transaction ID'],
  discount:   ['Discount Amount', 'Discount', 'Coupon Discount'],
  buyerId:    ['Buyer User ID', 'Buyer', 'Buyer Name'],
};

const DAY_MS = 24 * 60 * 60 * 1000;
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Apply Etsy's standard fee math to a single sale row (fallback). */
function estimateFeesForSale(row, settings) {
  const grossPerUnit = (row.itemTotal || 0) / Math.max(1, row.quantity || 1);
  const shippingPerUnit = (row.shipping || 0) / Math.max(1, row.quantity || 1);
  const transactionBase = grossPerUnit + shippingPerUnit;

  const listingFee = 0.20 * (row.quantity || 1);
  const transactionFee = transactionBase * 0.065 * (row.quantity || 1);

  const procRate = settings?.processingRate ?? 0.03;
  const procFlat = settings?.processingFlat ?? 0.25;
  const processingFee = (transactionBase * procRate) * (row.quantity || 1) + procFlat;

  let offsiteFee = 0;
  if (settings?.offsiteEnabled) {
    const rate = settings?.offsiteRate || 0.15;
    offsiteFee = Math.min(transactionBase * rate * (row.quantity || 1), 100);
  }

  const totalFees = listingFee + transactionFee + processingFee + offsiteFee;
  return { listingFee, transactionFee, processingFee, offsiteFee, totalFees };
}

/** Normalize a raw CSV row into a sale object. Returns null if row is unusable. */
function normalizeRow(rawRow) {
  const dateStr = pickField(rawRow, FIELD_MAP.date);
  const date = parseDate(dateStr);
  const itemName = pickField(rawRow, FIELD_MAP.itemName) || '(Unnamed item)';
  const quantity = parseAmount(pickField(rawRow, FIELD_MAP.quantity)) || 1;
  const itemTotal = parseAmount(pickField(rawRow, FIELD_MAP.itemTotal)) || 0;
  const shipping = parseAmount(pickField(rawRow, FIELD_MAP.shipping)) || 0;
  const orderNet = parseAmount(pickField(rawRow, FIELD_MAP.orderNet));
  const cardFees = parseAmount(pickField(rawRow, FIELD_MAP.cardFees));
  const salesTax = parseAmount(pickField(rawRow, FIELD_MAP.salesTax)) || 0;
  const discount = parseAmount(pickField(rawRow, FIELD_MAP.discount)) || 0;
  const status = (pickField(rawRow, FIELD_MAP.status) || 'Sold').toLowerCase();
  const currency = pickField(rawRow, FIELD_MAP.currency) || 'USD';
  const orderId = pickField(rawRow, FIELD_MAP.orderId) || '';
  const buyerId = pickField(rawRow, FIELD_MAP.buyerId) || '';

  if (itemTotal === 0 && shipping === 0) return null;

  const isRefund = status.includes('refund') || status.includes('cancel');

  return {
    date, dateStr, itemName, quantity, itemTotal, shipping,
    orderNet: Number.isFinite(orderNet) ? orderNet : null,
    cardFees: Number.isFinite(cardFees) ? cardFees : null,
    salesTax, discount, status, currency, orderId, buyerId, isRefund,
  };
}

/** Add fees + cost + profit columns to a normalized sale (mutates in place). */
function attachComputedFields(sale, settings, costOverrides) {
  if (sale.orderNet !== null && (sale.itemTotal + sale.shipping) > 0) {
    const grossSale = sale.itemTotal + sale.shipping;
    sale.totalFees = grossSale - sale.orderNet - sale.salesTax;
    sale.feesAreEstimated = false;
  } else {
    const est = estimateFeesForSale(sale, settings);
    sale.totalFees = est.totalFees;
    sale.feesAreEstimated = true;
    sale.feeBreakdown = est;
  }
  const totalCostPerUnit = (costOverrides && costOverrides[sale.itemName]) || 0;
  sale.totalCost = totalCostPerUnit * sale.quantity;
  sale.estimatedProfit = sale.itemTotal + sale.shipping - sale.totalFees - sale.totalCost;
}

// ─── Date / range helpers ────────────────────────────────────────────────

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

// endOfDay() helper removed — never used. Add it back when needed.

/** ISO-ish week key: the Monday of the week (YYYY-MM-DD). */
function isoWeekKey(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const day = x.getDay() || 7; // Sun=7
  if (day !== 1) x.setDate(x.getDate() - (day - 1));
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, '0');
  const dd = String(x.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

/** Format a bucket key as a human-readable label. */
function labelForBucket(key, granularity) {
  if (granularity === 'day' || granularity === 'week') {
    const [, m, d] = key.split('-');
    const monthName = MONTHS_SHORT[parseInt(m, 10) - 1];
    const dayNum = parseInt(d, 10);
    return granularity === 'week' ? `Wk ${monthName} ${dayNum}` : `${monthName} ${dayNum}`;
  }
  if (granularity === 'month') {
    const [y, m] = key.split('-');
    return `${MONTHS_SHORT[parseInt(m, 10) - 1]} '${y.slice(2)}`;
  }
  if (granularity === 'quarter') {
    const [y, q] = key.split('-');
    return `${q} ${y.slice(2)}`;
  }
  return key;
}

/** Return the bucket key string for a sale at the given granularity. */
function bucketKeyForDate(date, granularity) {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  if (granularity === 'day')   return `${y}-${m}-${d}`;
  if (granularity === 'week')  return isoWeekKey(date);
  if (granularity === 'month') return `${y}-${m}`;
  if (granularity === 'quarter') {
    const quarter = Math.floor(date.getMonth() / 3) + 1;
    return `${y}-Q${quarter}`;
  }
  return `${y}-${m}`;
}

/**
 * Suggest a sensible granularity given a date range span.
 *   ≤ 14 days → day · ≤ 90 days → week · ≤ 730 days → month · else quarter
 */
export function pickGranularity(range) {
  if (!range || !range.start || !range.end) return 'month';
  const days = Math.max(1, (range.end - range.start) / DAY_MS);
  if (days <= 14) return 'day';
  if (days <= 90) return 'week';
  if (days <= 730) return 'month';
  return 'quarter';
}

/**
 * Filter sales by date range (inclusive) and optionally by day-of-week.
 * @param {Array} sales - normalized sales array
 * @param {object|null} range - { start, end } Date range; null/undefined = no date filter
 * @param {Array<number>|null} daysOfWeek - subset of [0..6] (Sun=0..Sat=6) to keep;
 *                                          null/undefined or full set = no day filter
 */
export function filterSales(sales, range, daysOfWeek) {
  const hasRange = range && (range.start || range.end);
  const hasDayFilter = Array.isArray(daysOfWeek) && daysOfWeek.length > 0 && daysOfWeek.length < 7;

  if (!hasRange && !hasDayFilter) return sales;

  const lo = range && range.start ? range.start.getTime() : -Infinity;
  const hi = range && range.end   ? range.end.getTime()   : Infinity;
  const dowSet = hasDayFilter ? new Set(daysOfWeek) : null;

  return sales.filter(s => {
    if (!s.date) return false;
    const t = s.date.getTime();
    if (t < lo || t > hi) return false;
    if (dowSet && !dowSet.has(s.date.getDay())) return false;
    return true;
  });
}

/**
 * Compute the equivalent prior-period range for comparison.
 *   thisMonth → previous calendar month
 *   thisQuarter → previous calendar quarter
 *   thisYear / ytd → same window prior year
 *   anything else → equal-length window immediately preceding range.start
 */
export function computePriorRange(range) {
  if (!range || !range.start || !range.end) return null;

  if (range.preset === 'thisMonth') {
    const start = new Date(range.start.getFullYear(), range.start.getMonth() - 1, 1);
    const end = new Date(range.start.getFullYear(), range.start.getMonth(), 0, 23, 59, 59, 999);
    return { start: startOfDay(start), end, label: 'previous month' };
  }
  if (range.preset === 'thisQuarter') {
    const q = Math.floor(range.start.getMonth() / 3);
    const prevQYear = q === 0 ? range.start.getFullYear() - 1 : range.start.getFullYear();
    const startMonth = q === 0 ? 9 : (q - 1) * 3;
    const start = new Date(prevQYear, startMonth, 1);
    const end = new Date(prevQYear, startMonth + 3, 0, 23, 59, 59, 999);
    return { start: startOfDay(start), end, label: 'previous quarter' };
  }
  if (range.preset === 'thisYear' || range.preset === 'ytd') {
    const start = new Date(range.start);
    start.setFullYear(start.getFullYear() - 1);
    const end = new Date(range.end);
    end.setFullYear(end.getFullYear() - 1);
    return { start, end, label: 'same period last year' };
  }

  // Default: equal-length window immediately before range.start
  const span = range.end.getTime() - range.start.getTime();
  const priorEnd = new Date(range.start.getTime() - 1);
  const priorStart = new Date(priorEnd.getTime() - span);
  return { start: priorStart, end: priorEnd, label: 'prior equal period' };
}

/**
 * Group sales into time buckets and produce per-bucket aggregates.
 * Returns an array sorted by bucket key ascending:
 *   [{ bucket, label, revenue, fees, profit, orderCount }, ...]
 */
export function bucketSales(sales, granularity = 'month') {
  const validSales = sales.filter(s => !s.isRefund && s.date);
  const buckets = {};
  for (const s of validSales) {
    const key = bucketKeyForDate(s.date, granularity);
    if (!key) continue;
    if (!buckets[key]) {
      buckets[key] = {
        bucket: key,
        label: labelForBucket(key, granularity),
        revenue: 0, fees: 0, profit: 0, orderCount: 0,
      };
    }
    buckets[key].revenue += s.itemTotal + s.shipping;
    buckets[key].fees += s.totalFees || 0;
    buckets[key].profit += s.estimatedProfit || 0;
    buckets[key].orderCount += 1;
  }
  return Object.values(buckets).sort((a, b) => a.bucket.localeCompare(b.bucket));
}

/**
 * Aggregate already-normalized sales into the same shape that buildReport returns.
 * @param {object} opts.granularity - 'day' | 'week' | 'month' | 'quarter'
 * @param {boolean} opts.skipInsights - skip insight generation
 */
export function aggregateFromSales(sales, settings = {}, opts = {}) {
  const granularity = opts.granularity || 'month';

  if (!sales || sales.length === 0) {
    return {
      summary: null, byBucket: [], byMonth: [],
      topProducts: [], insights: [], sales: [],
    };
  }

  // Date range within this filtered set
  const dates = sales.map(s => s.date).filter(Boolean).sort((a, b) => a - b);
  const dateStart = dates[0];
  const dateEnd = dates[dates.length - 1];

  // Currency (most common)
  const currencyCounts = {};
  for (const s of sales) currencyCounts[s.currency] = (currencyCounts[s.currency] || 0) + 1;
  const currency = Object.entries(currencyCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'USD';

  // Summary aggregates
  const validSales = sales.filter(s => !s.isRefund);
  const refundedSales = sales.filter(s => s.isRefund);

  const totalRevenue = validSales.reduce((sum, s) => sum + s.itemTotal + s.shipping, 0);
  const totalFees = validSales.reduce((sum, s) => sum + (s.totalFees || 0), 0);
  const totalCosts = validSales.reduce((sum, s) => sum + (s.totalCost || 0), 0);
  const totalProfit = validSales.reduce((sum, s) => sum + (s.estimatedProfit || 0), 0);
  const totalDiscounts = sales.reduce((sum, s) => sum + (s.discount || 0), 0);
  const totalQuantity = validSales.reduce((sum, s) => sum + (s.quantity || 0), 0);
  const avgOrderValue = validSales.length > 0 ? totalRevenue / validSales.length : 0;
  const refundRate = sales.length > 0 ? refundedSales.length / sales.length : 0;
  const uniqueBuyers = new Set(validSales.map(s => s.buyerId).filter(Boolean)).size;
  const uniqueOrders = new Set(validSales.map(s => s.orderId).filter(Boolean)).size;
  const repeatBuyerRate = uniqueBuyers > 0 ? 1 - (uniqueOrders / Math.max(uniqueBuyers, 1)) : 0;
  const effectiveFeeRate = totalRevenue > 0 ? (totalFees / totalRevenue) * 100 : 0;
  const profitMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;
  const feesAreEstimated = validSales.some(s => s.feesAreEstimated);

  const summary = {
    rowCount: sales.length,
    validSaleCount: validSales.length,
    refundCount: refundedSales.length,
    totalRevenue, totalFees, totalCosts, totalProfit, totalDiscounts, totalQuantity,
    avgOrderValue, refundRate, uniqueBuyers, uniqueOrders, repeatBuyerRate,
    effectiveFeeRate, profitMargin, feesAreEstimated,
    currency, dateStart, dateEnd,
  };

  // Bucketed time series at active granularity + always-month for export/Excel
  const byBucket = bucketSales(sales, granularity);
  const byMonth = granularity === 'month' ? byBucket : bucketSales(sales, 'month');

  // Top products
  const productBucket = {};
  for (const s of validSales) {
    const key = s.itemName;
    if (!productBucket[key]) {
      productBucket[key] = {
        name: key, revenue: 0, fees: 0, profit: 0,
        quantity: 0, orderCount: 0, refundCount: 0,
      };
    }
    productBucket[key].revenue += s.itemTotal + s.shipping;
    productBucket[key].fees += s.totalFees || 0;
    productBucket[key].profit += s.estimatedProfit || 0;
    productBucket[key].quantity += s.quantity || 0;
    productBucket[key].orderCount += 1;
  }
  for (const s of refundedSales) {
    if (productBucket[s.itemName]) productBucket[s.itemName].refundCount += 1;
  }
  const topProducts = Object.values(productBucket).sort((a, b) => b.revenue - a.revenue);

  const insights = opts.skipInsights ? [] : generateInsights({
    byMonth, topProducts, totalRevenue, refundRate,
    effectiveFeeRate, feesAreEstimated, sales, currency,
  });

  return { summary, byBucket, byMonth, topProducts, insights, sales };
}

function generateInsights({ byMonth, topProducts, totalRevenue, refundRate, effectiveFeeRate, feesAreEstimated, sales, currency }) {
  const insights = [];

  if (byMonth.length > 1) {
    const best = byMonth.slice().sort((a, b) => b.revenue - a.revenue)[0];
    insights.push({
      type: 'best-month', icon: '📈',
      text: `Your best month was ${best.label} with ${currency} ${best.revenue.toFixed(0)} in revenue.`,
    });
  }

  if (topProducts.length > 0 && totalRevenue > 0) {
    const top = topProducts[0];
    const pct = (top.revenue / totalRevenue) * 100;
    if (pct > 20) {
      insights.push({
        type: 'product-concentration',
        icon: pct > 40 ? '⚠️' : '🎯',
        text: `"${top.name}" generates ${pct.toFixed(0)}% of total revenue. ${pct > 40 ? 'Heavy concentration — consider diversifying.' : 'Strong best-seller.'}`,
      });
    }
  }

  if (refundRate > 0.05) {
    insights.push({
      type: 'refund-rate', icon: '⚠️',
      text: `Refund rate of ${(refundRate * 100).toFixed(1)}% is above the 5% benchmark. Worth investigating which products are returning.`,
    });
  } else if (sales.length > 50 && refundRate < 0.02) {
    insights.push({
      type: 'low-refund', icon: '✓',
      text: `Refund rate of ${(refundRate * 100).toFixed(1)}% is excellent — well below industry average.`,
    });
  }

  if (effectiveFeeRate > 18) {
    insights.push({
      type: 'high-fees', icon: '💰',
      text: `Etsy is keeping ${effectiveFeeRate.toFixed(1)}% of revenue — likely because Offsite Ads is triggering. Worth reviewing.`,
    });
  }

  const mostRefunded = topProducts
    .filter(p => p.refundCount > 0)
    .sort((a, b) => b.refundCount - a.refundCount)[0];
  if (mostRefunded && mostRefunded.refundCount >= 3) {
    insights.push({
      type: 'product-refunds', icon: '⚠️',
      text: `"${mostRefunded.name}" had ${mostRefunded.refundCount} refunds — review its listing or quality.`,
    });
  }

  if (feesAreEstimated) {
    insights.push({
      type: 'estimation', icon: 'ℹ️',
      text: `Fee numbers are estimated using your calculator settings. Import a CSV with "Order Net" column for exact fees.`,
    });
  }

  return insights;
}

/**
 * Main entry: parse rows → normalize → compute fees → aggregate at month granularity.
 * The returned `sales` array can be filtered + re-aggregated cheaply via aggregateFromSales().
 */
export function buildReport(parsedRows, settings = {}, costOverrides = {}) {
  const sales = [];
  const errors = [];

  for (let i = 0; i < parsedRows.length; i++) {
    try {
      const sale = normalizeRow(parsedRows[i]);
      if (sale) sales.push(sale);
    } catch (e) {
      errors.push(`Row ${i + 2}: ${e.message}`);
    }
  }

  if (sales.length === 0) {
    return {
      meta: { rowCount: 0, errors: errors.concat(['No usable sales rows found in CSV']) },
      summary: null, byBucket: [], byMonth: [], topProducts: [], insights: [], sales: [],
    };
  }

  for (const sale of sales) {
    attachComputedFields(sale, settings, costOverrides);
  }

  const aggregated = aggregateFromSales(sales, settings, { granularity: 'month' });
  const csvHadOrderNet = !aggregated.summary?.feesAreEstimated;

  return {
    meta: { rowCount: sales.length, errors, csvHadOrderNet },
    ...aggregated,
  };
}
